/**
 * KeyboardInput Component
 * 
 * Text input field for keyboard mode that allows users to type messages instead of speaking.
 * - Auto-focuses when opened
 * - Submit on Enter key
 * - Mobile: keyboard covers history, dismisses after sending
 * - Desktop: fixed at bottom with scrollable history above
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { logger } from '../utils/logger';

interface KeyboardInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  isAIResponding?: boolean;
  onMessageSubmit?: () => void; // 🎯 Called when user submits message (to interrupt AI)
}

export default function KeyboardInput({
  onSendMessage,
  disabled = false,
  isAIResponding = false,
  onMessageSubmit
}: KeyboardInputProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const previousAIResponding = useRef(isAIResponding);
  const hasFocusedInitially = useRef(false);
  const [bottomPosition, setBottomPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafId = useRef<number | null>(null);
  const lastSentMessage = useRef(''); // Track last sent to avoid duplicates

  // Adjust position to stay above keyboard on mobile
  useEffect(() => {
    const updatePosition = () => {
      // Clear any pending updates
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }

      // Use requestAnimationFrame to ensure DOM values are stable
      rafId.current = requestAnimationFrame(() => {
        if (window.visualViewport && containerRef.current) {
          // Calculate offset from visual viewport bottom
          const visualBottom = window.visualViewport.height;
          const windowHeight = window.innerHeight;
          const keyboardHeight = windowHeight - visualBottom;
          
          // Get height of the input container itself
          const inputHeight = containerRef.current.offsetHeight;
          
          // Position input so it's fully visible above keyboard
          // Need to move up by: keyboard height + half of input height
          // This ensures the input is centered above the keyboard, not cut in half
          if (keyboardHeight > 50 && inputHeight > 0) {
            const offset = keyboardHeight + (inputHeight / 2);
            setBottomPosition(offset);
          } else {
            setBottomPosition(0);
          }
        }
      });
    };

    if (window.visualViewport) {
      updatePosition();
      window.visualViewport.addEventListener('resize', updatePosition);
      window.visualViewport.addEventListener('scroll', updatePosition);
      
      return () => {
        window.visualViewport?.removeEventListener('resize', updatePosition);
        window.visualViewport?.removeEventListener('scroll', updatePosition);
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
        }
      };
    }
  }, []);

  // Detect if we're on mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Auto-focus input when component mounts (DESKTOP ONLY)
  useEffect(() => {
    if (inputRef.current && !disabled && !hasFocusedInitially.current && !isMobile()) {
      // Only auto-focus on desktop - mobile causes keyboard flickering
      const focusTimer = setTimeout(() => {
        inputRef.current?.focus();
        hasFocusedInitially.current = true;
        logger.debug('[⌨️ Keyboard Mode] Initial focus set (desktop)');
      }, 300);
      
      return () => clearTimeout(focusTimer);
    }
  }, [disabled]);

  // Auto-focus when AI finishes responding (DESKTOP ONLY)
  useEffect(() => {
    // Check if AI just finished responding (transition from true to false)
    if (previousAIResponding.current && !isAIResponding && !disabled && !isMobile()) {
      logger.debug('[⌨️ Keyboard Mode] AI finished responding, auto-focusing input (desktop)');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
    previousAIResponding.current = isAIResponding;
  }, [isAIResponding, disabled]);


  const doActualSubmit = () => {
    // Read DIRECTLY from DOM input
    const msg = (inputRef.current?.value || '').trim();
    
    if (!msg || disabled) {
      return;
    }
    
    // Evitar enviar a mesma mensagem múltiplas vezes
    if (msg === lastSentMessage.current) {
      return;
    }
    
    onMessageSubmit?.();
    onSendMessage(msg);
    lastSentMessage.current = msg;
    
    // Clear both React state AND DOM input
    setMessage('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    
    // Close keyboard on mobile
    if (isMobile() && inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    doActualSubmit();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doActualSubmit();
    }
  };

  return (
    <div 
      ref={containerRef}
      className="fixed left-0 right-0 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 z-30"
      style={{ 
        bottom: `${bottomPosition}px`,
        paddingBottom: 'env(safe-area-inset-bottom)',
        transition: 'bottom 0.1s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <div className="w-full max-w-4xl mx-auto">
        <form onSubmit={handleFormSubmit} className="flex items-center gap-2 p-3 md:p-4">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => {
              const val = e.target.value;
              setMessage(val);
              // Clear last sent message when user types something new
              if (val.trim() !== lastSentMessage.current) {
                lastSentMessage.current = '';
              }
            }}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            placeholder={
              isAIResponding 
                ? t('scenario.session.keyboard.waitingForResponse') 
                : t('scenario.session.keyboard.placeholder')
            }
            className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-3 md:py-2 text-base md:text-sm border border-gray-600 focus:border-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            maxLength={500}
            autoCapitalize="sentences"
            autoCorrect="on"
            autoComplete="off"
          />
          {/* Hide button on mobile - use keyboard Enter instead */}
          <button
            type="submit"
            disabled={disabled || !message.trim()}
            className="hidden md:flex w-10 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 items-center justify-center transition-colors duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('scenario.session.keyboard.send')}
          >
            <PaperAirplaneIcon className="w-4 h-4 text-white" />
          </button>
        </form>
      </div>
    </div>
  );
}

